
import type { DailyKeywordData } from '@empathybroker/tsclient-stats/dist/models/daily-keywords/daily-keyword-data'
import type { KeywordsTrendsDataItem } from '@empathybroker/tsclient-stats/dist/models/keywords-trends/keywords-trends-data-item'
import type { KeywordData as KeywordsDataItem } from '@empathybroker/tsclient-stats/dist/models/keywords/keyword-data'
import type { QueriesResponse } from '@empathybroker/tsclient-stats/dist/models/queries/queries.response'
import type { QueryData } from '@empathybroker/tsclient-stats/dist/models/queries/query-data'
import type {
  Params,
  SortedStreamGraphConfig,
  SortedStreamGraphData,
  SortedStreamGraphDataItem,
  SortedStreamGraphDataItemValue,
} from '@empathyco/insights'
import type {
  Dictionary,
  KeywordData,
  KeywordsData,
  KeywordsMessages,
  SearchQueryData,
} from '../../models'
import { KeywordsTrendsModeEnum } from '@empathybroker/tsclient-stats/dist/enums/keywords-trends-mode.enum'

import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { SortedStreamGraphComponent } from '@empathyco/insights'

import { deepEqual } from '@empathyco/x-utils'
import { defineComponent } from 'vue'
import { mapActions } from 'vuex'
import InsightsNoResults from '../../components/insights/insights-no-results.component.vue'
import KeywordSuggestion from '../../components/insights/keywords/keyword-suggestion.component.vue'
import KeywordsAside from '../../components/insights/keywords/keywords-aside.component.vue'
import { EpbBadge } from '../../components/shared/epb-badge'
import { EpbText } from '../../components/shared/epb-text'
import { EpbToggle } from '../../components/shared/epb-toggle'

import SearchBox from '../../components/shared/search-box.component.vue'

import { useAppLoading } from '../../composables/use-app-loading'

import { FilterIdEnum, KeywordsModeEnum, KpiEnum } from '../../enums'

import { InsightsMixin } from '../../mixins'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'
enum DataGroupingType {
  DAILY,
  WEEKLY,
  MONTHLY,
}

export default defineComponent({
  name: 'Keywords',
  components: {
    EpbBadge,
    EpbText,
    EpbToggle,
    InsightsNoResults,
    SearchBox,
    KeywordsAside,
    KeywordSuggestion,
  },
  mixins: [InsightsMixin],
  setup() {
    const { loading } = useAppLoading()
    return { loading }
  },
  data() {
    return {
      maxKeywordsTop: 10,
      maxQueriesPerKeyword: 5,
      maxSearchesSaved: 10,
      mode: KeywordsModeEnum.GROWTH,
      component: undefined,
    }
  },
  computed: {
    searchResults(): KeywordData[] {
      if (!this.data || !this.context.query) {
        return []
      }
      const searchData = this.data.search.find(
        (queryData: SearchQueryData) => queryData.query === this.context.query,
      )
      return searchData ? searchData.results : []
    },
    searchSuggestions(): { value: string }[] {
      return this.searchResults.map((keywordData: KeywordData) => ({
        value: keywordData.overall.keyword,
      }))
    },
    isCompareMode(): boolean {
      return (
        !!this.data &&
        !!this.data.selected.length &&
        this.data.selected.some(
          (selectedKeywordData: KeywordData) =>
            !this.data.top[this.mode]?.some(
              (topKeywordData: KeywordData) =>
                topKeywordData.overall.keyword === selectedKeywordData.overall.keyword,
            ),
        )
      )
    },
    title(): string {
      if (this.isCompareMode) {
        return this.messages.main.titles.compare
      }
      return this.$epb.utils.compileMessage(this.messages.main.titles.top, {
        count: this.maxKeywordsTop,
        mode: this.messages.main.modes[this.mode],
      })
    },
    modeOptions(): { id: KeywordsModeEnum; content: string }[] {
      return Object.values(KeywordsModeEnum).map((id: KeywordsModeEnum) => ({
        id,
        content: (this.messages.main.modes[id] || id).toUpperCase(),
      }))
    },
    componentParams(): Params<SortedStreamGraphConfig, SortedStreamGraphData> {
      return {
        config: {
          locale: this.$epb.locale,
          itemNameWidth: 120,
          valueNameHeight: 40,
          valueNameWidth: 65,
          valueSuffix:
            this.$epb.messages.insights.analytics.kpis[KpiEnum.QUERIES].valueLabel.toUpperCase(),
          areaOpacity: 0.5,
          deselectIconCls: 'eicon',
          deselectIconContent: 'close',
        },
        data: this.getComponentData(),
      }
    },
    selectedKeywords(): KeywordData[] {
      return this.data?.selected || []
    },
    messages(): KeywordsMessages {
      return this.$epb.messages.insights.keywords
    },
    hasNoResults(): boolean {
      return !this.loading && this.componentParams.data.length === 0
    },
  },
  methods: {
    ...mapActions(CONTEXT_CONSTS.moduleName, {
      setQuery: CONTEXT_CONSTS.actions.setQuery,
    }),
    async fetchData(cleanData: boolean, keepSelected = true): Promise<KeywordsData> {
      const { top, search, selected } =
        cleanData || !this.data
          ? {
              top: {},
              search: [],
              selected: [],
            }
          : this.data
      if (!top[this.mode]) {
        top[this.mode] = await this.fetchTopData(this.mode, top)
      }
      if (this.context.query) {
        const queryAlreadySearched = search.some(
          (queryData: SearchQueryData) => queryData.query === this.context.query,
        )
        if (!queryAlreadySearched) {
          const queryKeywordsData = await this.fetchSearchData(this.context.query, top)
          search.unshift({
            query: this.context.query,
            results: queryKeywordsData,
          })
          search.splice(this.maxSearchesSaved)
        }
      }
      return {
        top,
        search,
        selected: keepSelected ? selected : [],
      }
    },
    async fetchTopData(
      mode: KeywordsModeEnum,
      top: Dictionary<KeywordData[]> = {},
    ): Promise<KeywordData[]> {
      const reportResponse = await this.fetchStatsReport({
        id:
          mode === KeywordsModeEnum.GROWTH
            ? StatsReportsIdsEnum.KEYWORDS_TRENDS
            : StatsReportsIdsEnum.KEYWORDS,
        params: {
          rows: this.maxKeywordsTop,
          ...(mode === KeywordsModeEnum.GROWTH && { mode: KeywordsTrendsModeEnum.SIGNIFICANCE }),
        },
      })
      return Promise.all(
        reportResponse.data.map((overall: KeywordsDataItem | KeywordsTrendsDataItem) =>
          this.fetchKeywordData(overall, top),
        ),
      )
    },
    async fetchKeywordData(
      overall: KeywordsDataItem | KeywordsTrendsDataItem,
      top: Dictionary<KeywordData[]>,
    ): Promise<KeywordData> {
      const topKeywordData = this.findKeywordDataInTop(overall.keyword, top)
      const { dist, queries } = topKeywordData || {
        dist: await this.fetchKeywordDistData(overall.keyword),
        queries: undefined,
      }
      return {
        overall,
        dist,
        queries,
      }
    },
    findKeywordDataInTop(keyword: string, top: Dictionary<KeywordData[]>): KeywordData {
      for (const topData of Object.values(top)) {
        const keywordData = topData.find(
          (topKeywordData: KeywordData) => topKeywordData.overall.keyword === keyword,
        )
        if (keywordData) {
          return keywordData
        }
      }
    },
    async fetchKeywordDistData(keyword: string): Promise<DailyKeywordData[]> {
      const reportResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.DAILY_KEYWORDS,
        params: {
          keyword,
        },
      })
      return reportResponse.data.dist
    },
    async fetchSearchData(
      query: string,
      top: Dictionary<KeywordData[]> = {},
    ): Promise<KeywordData[]> {
      const reportResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.KEYWORDS,
        params: {
          query,
        },
      })
      return Promise.all(
        reportResponse.data.map((overall: KeywordsDataItem) => this.fetchKeywordData(overall, top)),
      )
    },
    getSearchSuggestionData(value: string): KeywordData {
      return this.searchResults.find(
        (keywordData: KeywordData) => keywordData.overall.keyword === value,
      )
    },
    getComponentData(): SortedStreamGraphData {
      if (!this.data) {
        return []
      }
      const keywordsData = this.isCompareMode ? this.data.selected : this.data.top[this.mode] || []
      return keywordsData.map((keywordData: KeywordData) => ({
        name: keywordData.overall.keyword.toUpperCase(),
        values: this.transformComponentDataItemValues(keywordData),
        selected: this.data.selected.some(
          (selectedKeywordData: KeywordData) =>
            selectedKeywordData.overall.keyword === keywordData.overall.keyword,
        ),
      }))
    },
    transformComponentDataItemValues(keywordData: KeywordData): SortedStreamGraphDataItemValue[] {
      const startDate = keywordData.dist[0].date
      const endDate = keywordData.dist[keywordData.dist.length - 1].date
      const groupingType = this.getComponentDataGroupingType(startDate, endDate)
      let dataItemValue: SortedStreamGraphDataItemValue
      return keywordData.dist.reduce(
        (
          dataItemValues: SortedStreamGraphDataItemValue[],
          keywordDistDataItem: DailyKeywordData,
        ) => {
          const { name, tooltipName } = this.getComponentDataItemValueNames(
            groupingType,
            keywordDistDataItem.date,
            startDate,
            endDate,
          )
          if (!dataItemValue || name !== dataItemValue.name) {
            dataItemValue = {
              name,
              tooltipName,
              value: 0,
            }
            dataItemValues.push(dataItemValue)
          }
          dataItemValue.value += keywordDistDataItem.queryCount
          return dataItemValues
        },
        [],
      )
    },
    getComponentDataGroupingType(startDate: Date, endDate: Date): DataGroupingType {
      const periodDurationInDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      if (periodDurationInDays < 14) {
        return DataGroupingType.DAILY
      }
      if (periodDurationInDays < 57) {
        return DataGroupingType.WEEKLY
      }
      return DataGroupingType.MONTHLY
    },
    getComponentDataItemValueNames(
      groupingType: DataGroupingType,
      valueDate: Date,
      startDate: Date,
      endDate: Date,
    ): { name: string; tooltipName: string } {
      return {
        [DataGroupingType.DAILY]: this.getComponentDailyDataItemValueNames,
        [DataGroupingType.WEEKLY]: this.getComponentWeeklyDataItemValueNames,
        [DataGroupingType.MONTHLY]: this.getComponentMonthlyDataItemValueNames,
      }[groupingType](valueDate, startDate, endDate)
    },
    getComponentDailyDataItemValueNames(valueDate: Date): {
      name: string
      tooltipName: string
    } {
      const dayString = valueDate.toLocaleDateString(this.$epb.locale, {
        day: '2-digit',
      })
      const weekDayString = valueDate.toLocaleDateString(this.$epb.locale, {
        weekday: 'short',
      })
      const name = `${dayString}<br>${weekDayString}`.toUpperCase()
      const tooltipName = valueDate
        .toLocaleDateString(this.$epb.locale, {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        })
        .toUpperCase()
      return { name, tooltipName }
    },
    getComponentWeeklyDataItemValueNames(
      valueDate: Date,
      startDate: Date,
      endDate: Date,
    ): { name: string; tooltipName: string } {
      const weekDay = valueDate.getDay() === 0 ? 6 : valueDate.getDay() - 1
      const firstDayOfWeek = new Date(
        valueDate.getFullYear(),
        valueDate.getMonth(),
        valueDate.getDate() - weekDay,
      )
      const lastDayOfWeek = new Date(
        firstDayOfWeek.getFullYear(),
        firstDayOfWeek.getMonth(),
        firstDayOfWeek.getDate() + 6,
      )
      const firstDay = startDate.getTime() > firstDayOfWeek.getTime() ? startDate : firstDayOfWeek
      const lastDay = endDate.getTime() < lastDayOfWeek.getTime() ? endDate : lastDayOfWeek
      const isSameMonth = firstDay.getMonth() === lastDay.getMonth()
      const firstDayDayString = firstDay.toLocaleDateString(this.$epb.locale, {
        day: '2-digit',
      })
      const firstDayMonthString = firstDay.toLocaleDateString(this.$epb.locale, {
        month: 'short',
      })
      const firstDayString = firstDay.toLocaleDateString(this.$epb.locale, {
        day: '2-digit',
        month: 'short',
      })
      const lastDayString = lastDay.toLocaleDateString(this.$epb.locale, {
        day: '2-digit',
        month: !isSameMonth ? 'short' : undefined,
      })
      const name = (
        isSameMonth
          ? `${firstDayMonthString}<br>${firstDayDayString}-${lastDayString}`
          : `${firstDayString}-<br>${lastDayString}`
      ).toUpperCase()
      const isSameYear = firstDay.getFullYear() === lastDay.getFullYear()
      const firstDayMonthTooltip = firstDay.toLocaleDateString(this.$epb.locale, {
        month: 'short',
      })
      const firstDayStringTooltip = firstDay.toLocaleDateString(this.$epb.locale, {
        day: '2-digit',
        year: !isSameYear ? 'numeric' : undefined,
      })
      const lastDayStringTooltip = lastDay.toLocaleDateString(this.$epb.locale, {
        month: !isSameMonth ? 'short' : undefined,
        day: '2-digit',
      })
      const lastDayYearTooltip = lastDay.toLocaleDateString(this.$epb.locale, {
        year: 'numeric',
      })
      const tooltipName = `${firstDayMonthTooltip} ${firstDayStringTooltip}-${lastDayStringTooltip},
        ${lastDayYearTooltip}`.toUpperCase()
      return { name, tooltipName }
    },
    getComponentMonthlyDataItemValueNames(
      valueDate: Date,
      startDate: Date,
      endDate: Date,
    ): { name: string; tooltipName: string } {
      const valueDateString = valueDate.toLocaleDateString(this.$epb.locale, {
        month: 'short',
      })
      const valueDateTooltipString = valueDate.toLocaleDateString(this.$epb.locale, {
        month: 'short',
        year: 'numeric',
      })
      let name = valueDateString.toUpperCase()
      let tooltipName = valueDateTooltipString.toUpperCase()
      const firstDayOfMonth = new Date(valueDate.getFullYear(), valueDate.getMonth(), 1)
      const lastDayOfMonth = new Date(valueDate.getFullYear(), valueDate.getMonth() + 1, 0)
      const dateValueMessages = this.$epb.messages.filters.values[FilterIdEnum.DATE]
      if (startDate.getTime() > firstDayOfMonth.getTime()) {
        const startDateString = startDate.toLocaleDateString(this.$epb.locale, {
          day: '2-digit',
        })
        const from = this.$epb.utils.compileMessage(dateValueMessages.from, {
          date: startDateString,
        })
        name += `<br>(${from})`
        tooltipName += ` (${from})`
      } else if (endDate.getTime() < lastDayOfMonth.getTime()) {
        const endDateString = endDate.toLocaleDateString(this.$epb.locale, {
          day: '2-digit',
        })
        const to = this.$epb.utils.compileMessage(dateValueMessages.to, {
          date: endDateString,
        })
        name += `<br>(${to})`
        tooltipName += ` (${to})`
      }
      return { name, tooltipName }
    },
    async onContextChange(): Promise<void> {
      const filtersHaveChanged =
        !this.previousContext || !deepEqual(this.context.filters, this.previousContext.filters)
      await this.loadData(filtersHaveChanged)
    },
    async onQueryChange(query: string): Promise<void> {
      await this.setQuery(query || undefined)
      this.$emit('contextChanged')
    },
    async onSearchSuggestionSelect(value: string): Promise<void> {
      const keywordAlreadySelected = this.data.selected.some(
        (keywordData: KeywordData) => keywordData.overall.keyword === value,
      )
      if (!keywordAlreadySelected) {
        const keywordDataToSelect: KeywordData = this.searchResults.find(
          (keywordData: KeywordData) => keywordData.overall.keyword === value,
        )
        this.data.selected.unshift(keywordDataToSelect)
        if (!keywordDataToSelect.queries) {
          keywordDataToSelect.queries = await this.fetchQueriesData(value)
        }
      }
    },
    async fetchQueriesData(keyword: string): Promise<QueryData[]> {
      const queriesResponse: QueriesResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.QUERIES,
        params: {
          keywords: keyword,
          rows: this.maxQueriesPerKeyword,
        },
      })
      return queriesResponse.data
    },
    async onDataItemClick(dataItem: SortedStreamGraphDataItem): Promise<void> {
      const topKeywordData: KeywordData = (this.data.top[this.mode] || []).find(
        (keywordData: KeywordData) =>
          keywordData.overall.keyword.toLowerCase() === dataItem.name.toLowerCase(),
      )
      if (!!topKeywordData && !this.data.selected.includes(topKeywordData)) {
        this.data.selected.unshift(topKeywordData)
        if (!topKeywordData.queries) {
          topKeywordData.queries = await this.fetchQueriesData(topKeywordData.overall.keyword)
        }
      }
    },
    onDataItemDeselectClick(dataItem: SortedStreamGraphDataItem): void {
      this.data.selected = this.data.selected.filter(
        (keywordData: KeywordData) =>
          keywordData.overall.keyword.toLowerCase() !== dataItem.name.toLowerCase(),
      )
    },
  },
  watch: {
    mode: {
      async handler(): Promise<void> {
        await this.loadData(false, false)
      },
    },
    componentParams: {
      handler(params: Params<SortedStreamGraphConfig, SortedStreamGraphData>): void {
        this.component.update(params)
      },
      deep: true,
    },
  },
  created(): void {
    this.component = new SortedStreamGraphComponent()
    this.component.onDataItemClick = (dataItem: SortedStreamGraphDataItem) =>
      this.onDataItemClick(dataItem)
    this.component.onDataItemDeselectClick = (dataItem: SortedStreamGraphDataItem) =>
      this.onDataItemDeselectClick(dataItem)
  },
  mounted(): void {
    this.component.init(this.$refs.component, this.componentParams)
  },
  beforeDestroy(): void {
    this.component.destroy()
  },
})
